/**
 * **Le guide MeTube** ⟦A-44⟧ — le faux petit journal, débloqué au nœud gardé à 3 000 abonnés.
 *
 * ## Ce que cet écran est, et ce qu'il ne doit jamais devenir
 *
 * ⟦A-43⟧ pose la règle du jeu entier : *le jeu dit CE QUI SE PASSE, le guide dit POURQUOI*, et
 * son critère de tri est *« si une phrase pourrait figurer telle quelle dans un wiki, elle n'a
 * rien à faire dans le jeu »*. Cet écran est **l'endroit du wiki**. C'est le seul.
 *
 * ⚠️ Il ne doit donc jamais devenir une **interface d'aide** : pas d'infobulle contextuelle, pas
 * de « tu es bloqué ? », pas de lien depuis un écran vers l'article qui explique cet écran.
 * ⟦A-44⟧ le dit en une phrase — *« un faux petit journal de l'univers, diégétique, parodique
 * […]. Jamais une interface d'aide. »* Le joueur vient le lire parce qu'il l'a payé, pas parce
 * qu'un point d'interrogation l'y a poussé.
 *
 * ## Pourquoi il ne lit aucune constante du moteur
 *
 * Le brouillon (`docs/GUIDE-METUBE-BROUILLON.md`) posait la question et la laissait ouverte :
 * le guide **recopie**-t-il les chiffres (et vieillit) ou les **lit**-il (et reste juste) ? Il
 * les recopie, et il l'assume : `content/guide.json` porte un champ `mesure` qui dit d'où vient
 * chaque chiffre. Un article de presse cite une mesure et sa date ; il ne se réécrit pas tout
 * seul, et personne ne s'en étonne. Le **jeu**, lui, lit toujours ses constantes vivantes.
 *
 * 🔑 Le vrai bénéfice est ailleurs : un guide qui lirait le moteur pourrait, un jour, être tenté
 * de le *renseigner*. Celui-ci ne le peut pas — `guide.json` n'entre pas dans `EngineContent`.
 *
 * ## Le maillon 3
 *
 * Cet écran est joignable par **le bouton « 📰 Le guide » de la chambre**, qui n'apparaît qu'une
 * fois le nœud acheté, et par la route `/guide`, gardée par la même capacité. Sans bouton, il
 * serait le onzième écran que personne n'atteint.
 */

import { useState } from "react";

import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";

import { dossiersDuSommaire } from "./sommaire";

export function Guide() {
  const journal = contenu().guide;
  const [ouvert, setOuvert] = useState(0);
  // ⟦C60 · lot b⟧ Le sommaire d'un monde sans les systèmes que le ménage a coupés (voir `sommaire.ts`).
  const dossiers = dossiersDuSommaire(journal.dossiers, contenu());

  // Un `guide.json` vide est refusé par Zod (`dossiers` a un `.min(1)`), mais l'écran ne suppose
  // rien : il vaut mieux une page qui le dit qu'un rendu de `undefined`.
  const dossier = dossiers[ouvert] ?? dossiers[0];
  if (!dossier) return null;

  /* ⟦A-7 décision 4⟧ Un panneau posé sur la chambre, comme la Boutique et l'Arbre — on ne
     quitte pas la chambre pour lire le journal, on l'ouvre dessus. Échap referme. */
  return (
    <Panneau titre={`📰 ${journal.titre}`} large>
      <div className="journal-guide">
        <header className="jg-manchette">
          <p className="jg-sous-titre">{journal.sous_titre}</p>
        </header>

        <div className="jg-corps">
          {/* Le sommaire : c'est un journal, on choisit son article. */}
          <nav className="jg-sommaire" aria-label="Sommaire du guide">
            <ol>
              {dossiers.map((d, i) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className={`jg-entree${i === ouvert ? " lu" : ""}`}
                    aria-current={i === ouvert ? "true" : undefined}
                    onClick={() => setOuvert(i)}
                  >
                    <span className="jg-rubrique">{d.rubrique}</span>
                    <span className="jg-entree-titre">{d.titre}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>

          {/* L'article. `key` sur l'id : changer d'article remonte en haut et rejoue l'entrée,
              au lieu de laisser le lecteur au milieu du précédent.

              ⟦C57 · A-174, précision du 02/10⟧ L'article reste ENTIER à l'écran, sans « Les dessous » :
              le Guide se lit à tête reposée, hors d'une phase de jeu, et la règle « peu de texte »
              vise les phases de jeu, pas lui (Matt : « le guide, lui, il peut être complet »). Les
              champs `titre_court` et `chapo_court` de `guide.json` restent dans le contenu, inutilisés ici. */}
          <article className="jg-article" key={dossier.id}>
            <p className="jg-rubrique">{dossier.rubrique}</p>
            <h2>{dossier.titre}</h2>
            <p className="jg-chapo">{dossier.chapo}</p>
            {dossier.paragraphes.map((p, i) => (
              <p key={i} className="jg-paragraphe">
                {p}
              </p>
            ))}

            {dossier.encadre ? (
              <aside className="jg-encadre">
                <h3>{dossier.encadre.titre}</h3>
                <p>{dossier.encadre.texte}</p>
              </aside>
            ) : null}

            {/* ⟦A-43⟧ D'où vient le chiffre. C'est ce qui sépare un guide d'une rumeur — et c'est
                la seule ligne de cet écran qui parle du jeu depuis l'extérieur du jeu. */}
            {dossier.mesure ? <p className="jg-mesure">📐 {dossier.mesure}</p> : null}
          </article>
        </div>

        <footer className="jg-ours">
          <p>{journal.ours}</p>
        </footer>
      </div>
    </Panneau>
  );
}
